import AnalyticsService from 'cio/services/analytics';
import AccountService from 'cio/services/account';
import { inject as service } from '@ember/service';
import { alias } from '@ember/object/computed';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { STRIPE_ERROR_CODES } from 'cio/utils/errors/formatters/stripe';
import AuthService from 'cio/services/auth';
import MasqService from 'cio/services/masq';
import AlertsService from 'cio/services/alerts';
import BillingService, { MIN_ZIP_LENGTH, ZIP_INPUT_DEBOUNCE } from 'cio/services/billing';
import MomentService from 'cio/services/moment';
import RouterService from '@ember/routing/router-service';
import { tracked } from '@glimmer/tracking';
import CouponModel from 'cio/models/coupon';
import TierModel from 'cio/models/tier';
import PlanModel from 'cio/models/plan';
import SubscriptionModel from 'cio/models/subscription';
import BillingInfoModel from 'cio/models/billing-info';
import { task, timeout } from 'ember-concurrency';
import DS from 'ember-data';
import FeaturesService from 'ember-feature-flags/services/features';
import { isEmpty } from '@ember/utils';

export default class PaymentMethodsController extends Controller {
  queryParams = ['plan', 'code', 'suspended', 'reactivated', 'updatedCard'];

  @service auth!: AuthService;
  @service masq!: MasqService;
  @service alerts!: AlertsService;
  @service account!: AccountService;
  @service billing!: BillingService;
  @service moment!: MomentService;
  @service router!: RouterService;
  @service analytics!: AnalyticsService;
  @service features!: FeaturesService;
  @service store!: DS.Store;

  // initializing to null to avoid empty params added to URL when using
  // RouterService.transitionTo:
  @tracked plan: string | null = '';
  @tracked code: string | null = '';
  @tracked suspended: boolean | false = false;
  @tracked reactivated: boolean | false = false;
  @tracked updatedCard: boolean | false = false;
  @tracked showUpdateBillingInfoModal: boolean | false = false;
  @tracked showCancelAccountModal: boolean | false = false;

  setDefaults() {
    this.suspended = false;
    this.reactivated = false;
  }

  @alias('billing.billingInfo') billingInfo!: BillingInfoModel;
  @alias('billing.subscription') subscription!: SubscriptionModel;
  @alias('billing.plans') plans!: PlanModel[];
  @alias('billing.tiers') tiers!: TierModel[];
  @alias('billing.coupon') coupon!: CouponModel;

  @computed('billing.subscription.stripeCustomer.lastPaymentError')
  get isSpecificStripeError() {
    return this.billing.subscription?.stripeCustomer?.lastPaymentError ?? '' in STRIPE_ERROR_CODES;
  }

  @computed('isSpecificStripeError', 'billingInfo.lumpSum', 'subscription.hasFailedOverageCharge')
  get shouldShowBillingAlert() {
    let isLumpSumCustomerWithOverageInvoiceOverdue =
      this.billingInfo?.lumpSum && this.subscription?.hasFailedOverageCharge;
    return (
      this.billingInfo?.overdue && (Boolean(this.isSpecificStripeError) || isLumpSumCustomerWithOverageInvoiceOverdue)
    );
  }

  @action
  toggleShowCancelAccountModal() {
    this.showCancelAccountModal = true;
    this.analytics.track('account', 'cancellation_started');
  }

  @action
  async updateCardAction({ setupIntent }: { setupIntent: Record<string, any> }) {
    try {
      await this.billing.updateCard.perform({ setupIntent });
      this.showUpdateBillingInfoModal = false;
      this.updatedCard = true;
    } catch {
      this.showUpdateBillingInfoModal = false;
      this.updatedCard = false;
    }
  }

  @action
  hideSuspensionAndUpdateCard() {
    this.showUpdateBillingInfoModal = false;
    this.suspended = false;
  }

  getUpdatedTaxRateTask = task(
    { restartable: true },
    async ({ country, state, zipCode }: Record<string, string>, debounce: boolean) => {
      if ([country, state, zipCode].any(isEmpty) || zipCode.length < MIN_ZIP_LENGTH) {
        return undefined;
      }

      if (debounce) {
        await timeout(ZIP_INPUT_DEBOUNCE);
      }

      return this.billing.updateTaxRate.perform({ country, state, zipCode });
    },
  );
}
declare module '@ember/controller' {
  interface Registry {
    'billing.payment-methods': PaymentMethodsController;
  }
}
